import * as THREE from "three";
import type { Slug } from "../../lib/candidates.ts";
import type { LineMaterial } from "three/addons/lines/LineMaterial.js";
import type { Board } from "../board.ts";
import type { Sponsors } from "../sponsors.ts";
import type { Partners } from "../partners.ts";
import { PALETTE } from "./materials.ts";
import { addEdges } from "./edges.ts";
import { createRoom } from "./room.ts";
import { createStations } from "./stations.ts";
import { createBillboards } from "./billboards.ts";
import { createSponsors } from "./sponsors.ts";
import { createPartners, type PartnersRig } from "./partners.ts";
import { createCrowd, type Crowd } from "./crowd.ts";

export interface ForumScene {
  scene: THREE.Scene;
  stations: Map<Slug, THREE.Object3D>; // slug → station Group (for proximity)
  crowd: Crowd; // roaming party people — call crowd.update() each frame
  partners: PartnersRig; // partner-table logos — call partners.update() too
  dispose(): void;
}

// Assembles the room: white page, the 22-walled room shell, candidate booths,
// the expert + methodology wall billboards (from the board digest), and the
// sponsor walls; then draws black pen-edges on every solid mesh.
export function buildForumScene(
  lineMaterial: LineMaterial,
  board: Board,
  sponsors: Sponsors,
  partners: Partners,
): ForumScene {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(PALETTE.paper);

  scene.add(createRoom());
  const { group: stationsGroup, stations } = createStations();
  scene.add(stationsGroup);
  scene.add(createBillboards(board));
  scene.add(createSponsors(sponsors));
  const partnersRig = createPartners(partners);
  scene.add(partnersRig.group);

  const crowd = createCrowd();
  scene.add(crowd.group);

  // Collect target meshes FIRST — addEdges() parents a LineSegments2 (extends
  // Mesh); skip billboards/portraits/plates (userData.skipEdges).
  const targets: THREE.Mesh[] = [];
  scene.traverse((o: THREE.Object3D) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh === true && mesh.userData.skipEdges !== true) {
      targets.push(mesh);
    }
  });
  targets.forEach((mesh) => addEdges(mesh, lineMaterial));

  const dispose = () => {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    scene.traverse((o: THREE.Object3D) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) geometries.add(mesh.geometry);
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => materials.add(m));
      else if (mat) materials.add(mat);
    });
    materials.forEach((m) => {
      if (m === (lineMaterial as unknown as THREE.Material)) return; // caller owns it
      (m as unknown as { map?: THREE.Texture }).map?.dispose();
      m.dispose();
    });
    geometries.forEach((g) => g.dispose());
  };

  return { scene, stations, crowd, partners: partnersRig, dispose };
}
